import React from 'react';
import './Toast.css';
import './ToastAction.css';


interface ToastProps {
  message: string;
  itemName?: string;
  onClose: () => void;
  duration?: number;
  type?: 'success' | 'error' | 'delete';
  action?: {
    label: string;
    onClick: () => void;
  };
}


const Toast: React.FC<ToastProps> = ({ message, itemName, onClose, duration = 2500, type = 'success', action }) => {
  const [swiping, setSwiping] = React.useState(false);
  const [swipeOffset, setSwipeOffset] = React.useState(0);
  const [dismissed, setDismissed] = React.useState(false);
  const startX = React.useRef<number | null>(null);
  const lastX = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (dismissed) return;
    const timer = setTimeout(() => {
      setDismissed(true);
      setTimeout(onClose, 250);
    }, duration);
    return () => clearTimeout(timer);
  }, [onClose, duration, dismissed]);

  const handleTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
    lastX.current = e.touches[0].clientX;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (startX.current !== null) {
      const deltaX = e.touches[0].clientX - startX.current;
      if (Math.abs(deltaX) > 10) {
        setSwiping(true);
        setSwipeOffset(deltaX);
      }
      lastX.current = e.touches[0].clientX;
    }
  };
  const handleTouchEnd = () => {
    const delta = lastX.current !== null && startX.current !== null
      ? lastX.current - startX.current
      : 0;
    if (swiping && Math.abs(delta) > 60) {
      setDismissed(true);
      setTimeout(onClose, 250);
    } else {
      setSwipeOffset(0);
    }
    setSwiping(false);
    startX.current = null;
    lastX.current = null;
  };

  const handleClose = () => {
    setDismissed(true);
    setTimeout(onClose, 250);
  };

  return (
    <div
      className={`toast${swiping ? ' toast--swiping' : ''}${dismissed ? ' toast--dismissed' : ''}${type === 'error' ? ' toast--error' : ''}${type === 'delete' ? ' toast--delete' : ''}`}
      style={swiping ? { '--swipe-offset': `${swipeOffset}px` } as React.CSSProperties : undefined}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      role="alert"
      aria-live="assertive"
      onMouseDown={(e) => e.preventDefault()}
    >
      <span className="toast__message">
        {itemName ? <strong>{itemName}</strong> : null}{message ? ` ${message}` : null}
      </span>
      {action && (
        <button
          className="toast__action"
          onClick={(e) => {
            e.stopPropagation();
            action.onClick();
            onClose();
          }}
        >
          {action.label}
        </button>
      )}
      <button 
        className="toast__close" 
        onClick={handleClose} 
        aria-label="Dismiss notification"
        onMouseDown={(e) => e.preventDefault()}
      >×</button>
    </div>
  );
};

export default Toast;
